import { FormatAfterDate } from '../format/format-after-date';
import { LikeContext } from '../like-context';
import { OptionsPositionBadge } from '../options-positions-badge';
import { TextTooltip } from '../text-tooltip';
import { Button } from '../ui/button';
import { UserAvatar } from '../user-avatar';
import { RepliesList } from './replies-list';
import { ReplyInput } from './reply-input';
import { cn } from '@/client/utils';
import { Comment, CommentReplies, ContentType, LikeStatus } from '@/libs/sdk/api';
import useInViewport from 'ahooks/lib/useInViewport';
import type { BasicTarget } from 'ahooks/lib/utils/domTarget';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type RepliesItemProps = {
  className?: string;
  root?: BasicTarget<Element>;
  onViewChange?: (isInView: boolean) => void;
  comment: Comment | CommentReplies;
  postUuid: string;
};

export const RepliesItem = ({ className, root, onViewChange, comment, postUuid }: RepliesItemProps) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const parentUuid = useMemo(() => comment.parentUuid || comment.uuid, [comment.parentUuid, comment.uuid]);
  const rootUuid = useMemo(() => comment.rootUuid || comment.uuid, [comment.rootUuid, comment.uuid]);
  const isRootComment = useMemo(() => !comment.rootUuid, [comment.rootUuid]);
  useInViewport(ref, { root, callback: entry => onViewChange?.(entry.isIntersecting) });
  const replies = useMemo(() => (comment as Comment)?.replies, [comment]);
  const repliesTotalCount = useMemo(() => (comment as Comment)?.repliesTotalCount, [comment]);
  const timestamp = useMemo(() => (comment as Comment)?.timestamp ?? (comment as CommentReplies)?.createdAt, [comment]);
  const options = useMemo(() => {
    if (!(comment as Comment).positions) return [];
    return (
      (comment as Comment).positions.map(item => ({
        type: 'buy' as const,
        value: item.balance,
        label: item.tokenDescription ?? item.tokenName,
        marketAddress: item.marketAddress,
        optionAddress: item.tokenAddress,
        decimal: item.decimal,
      })) ?? []
    );
  }, [comment]);
  return (
    <div className={cn('py-3 rounded-md w-full', isRootComment && 'px-3', className)}>
      <div className="flex items-center justify-between gap-2 w-full">
        <UserAvatar avatar={comment.userAvatarUrl} address={comment.uid} className="size-8" />
        <div className="space-y-1 flex-1 w-full overflow-hidden">
          <div className="flex items-center">
            {comment.userName && <span className="text-3.5 leading-5 font-medium truncate">{comment.userName}</span>}
            <OptionsPositionBadge options={options} className="flex-shrink-0" follow={false} />
          </div>
          <div className="text-xs text-foreground/40 font-normal leading-none">
            <FormatAfterDate time={timestamp} />
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 mt-3">
          <LikeContext
            isLinked={comment.isLike === LikeStatus.yes}
            contentUuid={comment.uuid}
            contentType={ContentType.comment}
            count={comment.likeCount}
            className="fill-[#536471] !px-1 hover:bg-transparent"
          />
          <Button
            variant="ghost"
            onClick={() => {
              setOpen(!open);
            }}
            className="text-[#536471] !text-3.5 leading-none shadow-none !px-1 hover:bg-transparent"
          >
            {t('reply')}
          </Button>
        </div>
      </div>
      {!isRootComment && (
        <TextTooltip tooltip={t('reply')} className="text-xs text-foreground/40 font-normal leading-none line-clamp-1 mt-3">
          {t('reply')} @{comment.parentUserName}
        </TextTooltip>
      )}
      <div className="text-base leading-6 font-normal text-foreground">{comment.content}</div>
      <ReplyInput
        open={open}
        userUid={comment.uid}
        userName={comment.userName}
        postUuid={postUuid}
        rootUuid={rootUuid}
        parentUuid={parentUuid}
        className={cn(open ? '' : '!hidden', isRootComment ? '' : 'mt-3')}
        onCached={() => setOpen(false)}
      />
      {isRootComment && (
        <RepliesList postUuid={postUuid} rootUuid={rootUuid} replies={replies} repliesTotalCount={repliesTotalCount} />
      )}
    </div>
  );
};
